import { useGetBriefTaskForms } from "@entities/brief/lib/hooks/campaigns/useGetBriefTaskForms";
import { useUploadTaskContent } from "@entities/campaigns/lib/hooks/useUploadTaskContent";
import { ToastService } from "@shared/lib/services";
import { IGetTaskForm } from "@shared/lib/types/briefCampains";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Flex } from "antd";
import { CheckCircle, Search } from "lucide-react";
import { useEffect, useState } from "react";
import styles from "./CopyFromOthers.module.scss";

type BriefFormValues = {
  description: string;
};

interface CopyFromOthersProps {
  briefId: string;
  deliverableConfigRefId: string;
  taskConfigRefId: string;
  campaignInfluencerId?: string;
  taskId?: string;
  onSuccess?: () => void;
  refetch?: () => void;
}

export function CopyFromOthers({
  briefId,
  deliverableConfigRefId,
  taskConfigRefId,
  campaignInfluencerId,
  taskId,
  onSuccess,
  refetch,
}: CopyFromOthersProps) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { forms, isFetching } = useGetBriefTaskForms({
    pageNo: 1 as 1,
    limit: 100 as 100,
    searchQuery: search,
    deliverableConfigRefId,
    taskConfigRefId,
    briefId,
  });

  const { uploadTaskContent, isUploading, isUploaded } = useUploadTaskContent(
    briefId,
    refetch
  );

  useEffect(() => {
    if (isUploaded) {
      ToastService.success("Brief copied successfully");
      onSuccess?.();
    }
  }, [isUploaded, onSuccess]);

  const handleSelect = () => {
    const selected: IGetTaskForm | null =
      items.find((i) => i._id === selectedId) || null;
    const taskForm = selected?.taskForm || null;
    if (!taskForm) return;

    // If campaignInfluencerId and taskId are provided, upload directly
    if (campaignInfluencerId && taskId) {
      const payload = {
        campaignInfluencerId,
        deliverableConfigRefId,
        taskId,
        contentUrls: Array.isArray(taskForm?.contentUrls)
          ? taskForm.contentUrls
          : [],
        description: taskForm?.description?.trim() || undefined,
        title: taskForm?.title?.trim() || undefined,
      };
      uploadTaskContent({ data: payload });
    }
  };

  const items = forms?.data ? forms.data : [];

  useEffect(() => {
    setSelectedId(null);
  }, [search]);

  return (
    <div className={styles.copyFromOthers}>
      <div className={styles.content}>
        <p className={styles.description}>
          Select a creator to copy their brief, instructions, and attachments.
        </p>

        <div className={styles.searchWrapper}>
          <Search size={14} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search creators..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.listContainer}>
          {isFetching && items.length === 0 && (
            <div className={styles.emptyState}>Loading...</div>
          )}
          {!isFetching && items.length === 0 && (
            <div className={styles.emptyState}>No creators found.</div>
          )}
          {items.length > 0 && (
            <div className={styles.list}>
              {items.map((it) => {
                const id = it._id;
                const name =
                  it.influencerName || it.influencer?.firstName || "Influencer";
                const isSelected = selectedId === id;
                return (
                  <div
                    key={id}
                    onClick={() => setSelectedId(id)}
                    className={`${styles.listItem} ${
                      isSelected ? styles.listItemSelected : ""
                    }`}
                  >
                    <img
                      src={it.socialMedia?.profilePicUrl}
                      alt=""
                      className={styles.avatar}
                    />
                    <Flex
                      justify="space-between"
                      align="center"
                      style={{ width: "100%" }}
                    >
                      <div>
                        <div className={styles.name}>{name}</div>

                        <TextWithTooltip
                          text={it.taskForm?.description || ""}
                          textClassName={styles.descriptionText}
                          limit={100}
                        />
                      </div>

                      {isSelected && (
                        <CheckCircle size={16} className={styles.checkIcon} />
                      )}
                    </Flex>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <button onClick={() => onSuccess?.()} className={styles.cancelButton}>
          Cancel
        </button>
        <button
          onClick={handleSelect}
          disabled={!selectedId || isUploading}
          className={styles.copyButton}
        >
          {isUploading ? "Copying..." : "Copy Requirements"}
        </button>
      </div>
    </div>
  );
}

export default CopyFromOthers;
